import type React from "react"

export type FocusTarget =
  | React.RefObject<HTMLElement | null>
  | (() => HTMLElement | null)

const focusableSelector = [
  "a[href]",
  "area[href]",
  "button",
  "input:not([type='hidden'])",
  "select",
  "textarea",
  "iframe",
  "summary",
  "[contenteditable='true']",
  "[tabindex]"
].join(",")

function isFocusable(element: HTMLElement) {
  return (
    element.tabIndex >= 0 &&
    !element.matches(":disabled") &&
    !element.closest("[inert]") &&
    // jsdom doesn't implement checkVisibility
    (element.checkVisibility?.({ visibilityProperty: true }) ?? true)
  )
}

function resolveFocusTarget(focusTarget?: FocusTarget) {
  if (!focusTarget) return null

  return typeof focusTarget === "function" ? focusTarget() : focusTarget.current
}

function findNextFocusableElement(element: HTMLElement) {
  const scope =
    element.closest<HTMLElement>(
      "dialog, [role='dialog'], [role='alertdialog']"
    ) ?? element.ownerDocument.body

  const focusableElements = Array.from(
    scope.querySelectorAll<HTMLElement>(focusableSelector)
  ).filter(candidate => !element.contains(candidate) && isFocusable(candidate))

  const nextFocusableElement = focusableElements.find(
    candidate =>
      element.compareDocumentPosition(candidate) &
      Node.DOCUMENT_POSITION_FOLLOWING
  )

  return (
    nextFocusableElement ??
    focusableElements[focusableElements.length - 1] ??
    null
  )
}

export function restoreFocusBeforeUnmount(
  element: HTMLElement,
  focusTarget?: FocusTarget
) {
  // Focus outside the element isn't lost when it unmounts, so leave it where it is
  if (!element.contains(element.ownerDocument.activeElement)) return

  const target =
    resolveFocusTarget(focusTarget) ?? findNextFocusableElement(element)

  target?.focus()
}
